import { GraduationCap, Plus, Radar, Trash2, TrendingUp, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { LIMITS } from '../../core/data/limits';
import { groupedRoleTracks } from '../../core/data/roleKeywords';
import { AUDIENCE_LABELS } from '../../core/scoring';
import {
  AUDIENCE_MODES,
  type AudienceMode,
  type ExperienceEntry,
  type ProfileInput,
  type Report,
  type SectionId,
} from '../../core/types';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { CheckField, SelectField, TextAreaField, TextField } from '../../components/ui/Field';
import { Segmented } from '../../components/ui/Segmented';
import { cn } from '../../lib/cn';
import { scoreTone } from '../../lib/tone';
import { SkillsInput } from './SkillsInput';

/**
 * The profile form.
 *
 * Every field is a paste target rather than something to compose from scratch:
 * the fastest route to a first score is to copy each section straight off a
 * LinkedIn profile, so labels and placeholders are written to make that obvious.
 *
 * Section ids (`section-headline` and friends) are the jump targets the score
 * panel's section bars link to, so clicking a weak section scrolls to the field
 * that fixes it.
 */

/** Deterministic, collision-free id for a new experience entry. */
function nextEntryId(existing: readonly ExperienceEntry[]): string {
  const taken = new Set(existing.map((entry) => entry.id));
  let n = existing.length + 1;
  while (taken.has(`entry-${n}`)) n += 1;
  return `entry-${n}`;
}

/**
 * A glyph per audience, so the four tiles are scannable at a glance rather than
 * four paragraphs to read.
 *
 * This lives here and not beside `AUDIENCE_LABELS` in `src/core/scoring.ts`
 * deliberately: `core/` is the pure engine and imports nothing from React or the
 * UI. A `lucide-react` import there would drag the icon library into the
 * scoring tests and into anything that ever reuses the engine headlessly.
 */
const AUDIENCE_ICONS: Record<AudienceMode, ReactNode> = {
  student: <GraduationCap />,
  jobSeeker: <Radar />,
  professional: <TrendingUp />,
  recruiter: <Users />,
};

function ScorePip({ score }: { score: number | null }) {
  if (score === null) {
    return <span className="text-xs font-medium text-subtle">not scored</span>;
  }
  const tone = scoreTone(score);
  return (
    <span className={cn('text-sm font-semibold tabular-nums', tone.text)}>
      {score}
      <span className="ml-0.5 text-xs font-normal text-subtle">/100</span>
    </span>
  );
}

export interface ProfileFormProps {
  profile: ProfileInput;
  mode: AudienceMode;
  report: Report;
  onChange: (profile: ProfileInput) => void;
  onModeChange: (mode: AudienceMode) => void;
}

export function ProfileForm({ profile, mode, report, onChange, onModeChange }: ProfileFormProps) {
  const patch = (changes: Partial<ProfileInput>) => onChange({ ...profile, ...changes });

  const sectionScore = (section: SectionId) =>
    report.sections.find((entry) => entry.section === section)?.score ?? null;

  const patchEntry = (id: string, changes: Partial<ExperienceEntry>) =>
    patch({
      experience: profile.experience.map((entry) =>
        entry.id === id ? { ...entry, ...changes } : entry
      ),
    });

  const addEntry = () =>
    patch({
      experience: [
        ...profile.experience,
        { id: nextEntryId(profile.experience), title: '', organisation: '', description: '' },
      ],
    });

  const removeEntry = (id: string) =>
    patch({ experience: profile.experience.filter((entry) => entry.id !== id) });

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------- audience --- */}
      <Card>
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="Who is this profile for?"
            description="This reweights the scoring rather than changing the advice — a student is judged on different things from a hiring manager."
          />
          <Segmented
            label="Audience"
            value={mode}
            onChange={onModeChange}
            options={AUDIENCE_MODES.map((value) => ({
              value,
              label: AUDIENCE_LABELS[value].label,
              description: AUDIENCE_LABELS[value].blurb,
              icon: AUDIENCE_ICONS[value],
            }))}
          />

          <SelectField
            label="Target role"
            hint="Used for keyword coverage. Leave it unset and we simply skip those checks rather than guessing."
            value={profile.targetRole}
            onChange={(event) => patch({ targetRole: event.target.value })}
          >
            <option value="">Not specified</option>
            {groupedRoleTracks().map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.tracks.map((track) => (
                  <option key={track.id} value={track.id}>
                    {track.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </SelectField>
        </CardBody>
      </Card>

      {/* ------------------------------------------------------- headline --- */}
      <Card id="section-headline">
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="Headline"
            description="The line under your name. It follows you into every search result, comment, and connection request."
            aside={<ScorePip score={sectionScore('headline')} />}
          />
          <TextField
            label="Your headline"
            hint={`Front-load the first ~${LIMITS.headline.previewTarget} characters — on mobile and in search results that is roughly all that shows.`}
            placeholder="Data Analyst · SQL & Python · Turning messy data into decisions"
            limit={{ hard: LIMITS.headline.hard, min: LIMITS.headline.minRecommended }}
            value={profile.headline}
            onChange={(event) => patch({ headline: event.target.value })}
          />
        </CardBody>
      </Card>

      {/* ---------------------------------------------------------- about --- */}
      <Card id="section-about">
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="About"
            description="Your summary. The one place on the profile you fully control the narrative."
            aside={<ScorePip score={sectionScore('about')} />}
          />
          <TextAreaField
            label="Your About section"
            hint={`Only the first ~${LIMITS.about.seeMoreTarget} characters show before “…see more”, so open with something worth clicking. End with how to reach you.`}
            placeholder={
              'I help teams find the story in their data.\n\nLast year I rebuilt our weekly reporting in dbt and cut the turnaround from three days to four hours…\n\nHappy to talk about analytics engineering — reach me at you@example.com.'
            }
            rows={9}
            limit={{ hard: LIMITS.about.hard, min: LIMITS.about.minRecommended }}
            value={profile.about}
            onChange={(event) => patch({ about: event.target.value })}
          />
        </CardBody>
      </Card>

      {/* ----------------------------------------------------- experience --- */}
      <Card id="section-experience">
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="Experience"
            description="Roles, internships, and substantial projects. Paste your bullets exactly as they are — one per line."
            aside={<ScorePip score={sectionScore('experience')} />}
          />

          {profile.experience.length === 0 ? (
            <p className="rounded-field border border-dashed border-line px-4 py-6 text-center text-sm text-muted">
              No entries yet. Add your most recent role first — it carries the most weight.
            </p>
          ) : null}

          <ol className="space-y-4">
            {profile.experience.map((entry, index) => (
              <li key={entry.id} className="rounded-field border border-line bg-sunken p-3.5">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold tracking-wide text-subtle uppercase">
                    {index === 0 ? 'Most recent' : `Entry ${index + 1}`}
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 />}
                    onClick={() => removeEntry(entry.id)}
                    className="text-critical-600 hover:bg-critical-50 dark:text-critical-400 dark:hover:bg-critical-950"
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <TextField
                    label="Job title"
                    placeholder="Data Analyst Intern"
                    value={entry.title}
                    onChange={(event) => patchEntry(entry.id, { title: event.target.value })}
                  />
                  <TextField
                    label="Organisation"
                    placeholder="Acme Analytics"
                    value={entry.organisation}
                    onChange={(event) => patchEntry(entry.id, { organisation: event.target.value })}
                  />
                </div>

                <TextAreaField
                  containerClassName="mt-3"
                  label="What you did"
                  hint="One bullet per line. Open each with a verb and finish with a number wherever you honestly can."
                  placeholder={
                    'Rebuilt the weekly sales report in SQL, cutting turnaround from 3 days to 4 hours\nAutomated data quality checks across 12 pipelines, catching 40+ issues before release'
                  }
                  rows={5}
                  limit={{ hard: LIMITS.roleDescription.hard }}
                  value={entry.description}
                  onChange={(event) => patchEntry(entry.id, { description: event.target.value })}
                />
              </li>
            ))}
          </ol>

          <Button variant="secondary" icon={<Plus />} onClick={addEntry}>
            Add {profile.experience.length === 0 ? 'an entry' : 'another entry'}
          </Button>
        </CardBody>
      </Card>

      {/* --------------------------------------------------------- skills --- */}
      <Card id="section-skills">
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="Skills"
            description="How recruiters filter. This is the one list where the exact wording matters more than the prose."
            aside={<ScorePip score={sectionScore('skills')} />}
          />
          <SkillsInput skills={profile.skills} onChange={(skills) => patch({ skills })} />
        </CardBody>
      </Card>

      {/* --------------------------------------------------- completeness --- */}
      <Card id="section-completeness">
        <CardBody className="space-y-4">
          <CardHeader
            as="h2"
            title="The rest of your profile"
            description="Things we cannot read from pasted text. Answer honestly — nobody sees this but you."
            aside={<ScorePip score={sectionScore('completeness')} />}
          />

          <fieldset className="space-y-3">
            <legend className="sr-only-live">Profile completeness checklist</legend>

            <CheckField
              label="I have a profile photo"
              hint="A profile with a photo gets far more engagement. It does not need to be a studio portrait — a clear, well-lit head and shoulders is enough."
              checked={profile.checklist.hasPhoto}
              onChange={(event) =>
                patch({ checklist: { ...profile.checklist, hasPhoto: event.target.checked } })
              }
            />
            <CheckField
              label="I have a background banner"
              hint="The wide image behind your photo. Even a plain one reads as a finished profile."
              checked={profile.checklist.hasBanner}
              onChange={(event) =>
                patch({ checklist: { ...profile.checklist, hasBanner: event.target.checked } })
              }
            />
            <CheckField
              label="My profile URL is customised"
              hint="linkedin.com/in/your-name rather than a string of digits. Takes thirty seconds and goes on your CV."
              checked={profile.checklist.hasCustomUrl}
              onChange={(event) =>
                patch({ checklist: { ...profile.checklist, hasCustomUrl: event.target.checked } })
              }
            />
            <CheckField
              label="My location is set"
              hint="Most recruiter searches filter by location first. Without one you are excluded before anyone reads a word."
              checked={profile.checklist.hasLocation}
              onChange={(event) =>
                patch({ checklist: { ...profile.checklist, hasLocation: event.target.checked } })
              }
            />
            <CheckField
              label="I have something in Featured"
              hint="A project, repo, article, or portfolio pinned to the top of the profile."
              checked={profile.checklist.hasFeatured}
              onChange={(event) =>
                patch({ checklist: { ...profile.checklist, hasFeatured: event.target.checked } })
              }
            />

            {mode === 'student' || mode === 'jobSeeker' ? (
              <CheckField
                label="Open to Work is switched on"
                hint="You can show it to recruiters only, rather than adding the green frame, if you would rather be discreet."
                checked={profile.checklist.openToWork}
                onChange={(event) =>
                  patch({ checklist: { ...profile.checklist, openToWork: event.target.checked } })
                }
              />
            ) : null}

            {mode === 'recruiter' ? (
              <CheckField
                label="My Hiring frame is switched on"
                hint="Signals to candidates that you are actively recruiting."
                checked={profile.checklist.hiring}
                onChange={(event) =>
                  patch({ checklist: { ...profile.checklist, hiring: event.target.checked } })
                }
              />
            ) : null}
          </fieldset>

          <TextField
            label="Recommendations received"
            type="number"
            min={0}
            max={99}
            inputMode="numeric"
            hint={`Aim for at least ${LIMITS.recommendations.minRecommended}. A short one from a supervisor or a lecturer is worth more than a long one from a peer.`}
            value={String(profile.checklist.recommendationCount)}
            onChange={(event) => {
              const parsed = Number.parseInt(event.target.value, 10);
              patch({
                checklist: {
                  ...profile.checklist,
                  // An empty or non-numeric field reads as 0 rather than NaN,
                  // which would propagate into the score.
                  recommendationCount: Number.isFinite(parsed)
                    ? Math.max(0, Math.min(99, parsed))
                    : 0,
                },
              });
            }}
            className="max-w-32"
          />
        </CardBody>
      </Card>
    </div>
  );
}
